import {
  isApolloError,
  type ApolloCache,
  type ApolloClient,
  type NormalizedCacheObject,
  type Reference,
  type StoreObject,
} from "@apollo/client";
import { createSelector } from "@reduxjs/toolkit";
import { parseISO } from "date-fns";
import { ApolloError } from "@apollo/client/errors";

import {
  CheckEndorsementEligibilityDocument,
  CheckEndorsementUnderwritingDocument,
  CreateOrUpdateEndorsementDocument,
  DiscardEndorsementsDocument,
  EndorsementStatus,
  EndorsementUnderwritingDecision,
  GetEndorsementDocument,
  IssueEndorsementDocument,
  type CheckEndorsementEligibilityQuery,
  type CheckEndorsementUnderwritingQuery,
  type CheckEndorsementUnderwritingQueryVariables,
  type CreateOrUpdateEndorsementInput,
  type CreateOrUpdateEndorsementMutation,
  type CreateOrUpdateEndorsementMutationVariables,
  type DiscardEndorsementsMutation,
  type DiscardEndorsementsMutationVariables,
  type GetEndorsementQuery,
  type GetEndorsementQueryVariables,
  type IssueEndorsementMutation,
  type IssueEndorsementMutationVariables,
} from "@covertree/agent-portal-graphql";
import { datadogRum } from "@datadog/browser-rum";
import { Toast } from "@covertree/utils";

import { AGENT_HELP_ADDRESS } from "@consts/index";

import { getGraphQLErrorType } from "@utils/graphQLErrorType";
import { createAsyncThunkSlice, typedReducerCreator } from "@redux/utils";

import {
  buildInsuredTabOverlayFromDraft,
  buildInsuredTabProjection,
} from "../basicEndorsementDetails";

import { buildEndorsementInput } from "./buildEndorsementInput";
import { projectUnderwriting } from "./projectUnderwriting";
import { mapFailureReasonToIssueStatus } from "./mapFailureReasonToIssueStatus";
import {
  isUnderwritingFailed,
  needsUnderwritingReview,
} from "./isUnderwritingFailed";
import { IssueEndorsementStatus } from "./types";

import type {
  CreateOrUpdateEndorsementArgs,
  EndorsementData,
  EndorsementFormPayload,
  EndorsementMode,
  EndorsementState,
  RootWithEndorsement,
  ShowLoadingFramePayload,
} from "./types";

/** Refreshed price change returned by a reprice, or null when unavailable. */
type EndorsementPriceChangePayload = NonNullable<
  CreateOrUpdateEndorsementMutation["createOrUpdateEndorsement"]["priceChange"]
> | null;

/**
 * Maps a Socotra price change onto the slice's delta fields. The big footer
 * figure is the prorated gross premium change; taxes and fees are reported
 * explicitly by the backend.
 *
 * An absent price change means the backend could **not** price the draft, not
 * that the premium held steady: `fetchEndorsementPriceChange` swallows Socotra
 * failures into `undefined`, which serialises over the wire as `null`, while the
 * mutation still reports `isSuccessful: true`. A genuine no-change arrives as an
 * object whose `premium` is `0`.
 *
 * So an absent price leaves the deltas untouched and raises `isPriceUnavailable`
 * instead of zeroing them. Zeroing reported a wrong number to the agent —
 * `formatPremiumDelta` renders `null` as "$0.00", indistinguishable from a real
 * zero — and, because `lastConfirmedDelta` was overwritten too, not even
 * `revertPremiumDelta` could recover the last good figure.
 */
const applyPriceChange = (
  state: EndorsementState,
  priceChange: EndorsementPriceChangePayload | undefined
) => {
  if (!priceChange) {
    state.isPriceUnavailable = true;
    return;
  }

  state.isPriceUnavailable = false;
  state.premiumDelta = priceChange.premium;
  state.lastConfirmedDelta = state.premiumDelta;
  state.deltaTax = priceChange.taxes;
  state.deltaFees = priceChange.fees;
};

/**
 * Creates or updates the draft endorsement and returns the refreshed data.
 * Both save and reprice use the unified `createOrUpdateEndorsement` mutation,
 * which `endorsementLocator` discriminates: absent opens a new draft on
 * `policyLocator`, present updates the existing draft. Either way the backend
 * reprices the draft and leaves it un-issued, returning the `priceChange` and the
 * projected unit- and policy-level coverages.
 *
 * The full Insured/Coverage selection is sent (via `buildEndorsementInput`) — the
 * backend diffs internally, so re-sending unchanged values is a no-op.
 */
const saveAndRepriceEndorsement = async ({
  client,
  values,
  unitId,
  endorsementId,
  policyLocator,
  existingEndorsement,
  savedValues,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  values: EndorsementFormPayload;
  unitId?: string;
  endorsementId: string | null;
  policyLocator: string | null;
  existingEndorsement?: EndorsementData;
  savedValues?: Partial<EndorsementFormPayload>;
}): Promise<EndorsementData> => {
  const locator = existingEndorsement?.endorsementId ?? endorsementId;

  // `buildEndorsementInput` sends `policyLocator` whenever no endorsement locator
  // exists yet, so a reset slice (see `resetEndorsement` / `discardEndorsements`,
  // both returning `initialState`) would post `policyLocator: null`. Fail here
  // rather than let the backend reject an unaddressed mutation.
  if (!locator && !policyLocator) {
    throw new Error(
      "This endorsement is no longer addressed to a policy. Please reopen it from the policy page."
    );
  }

  const input = buildEndorsementInput({
    values,
    endorsementLocator: locator,
    policyLocator,
    unitId,
    unitLevelCoverages: existingEndorsement?.unitLevelCoverages,
    policyLevelCoverages: existingEndorsement?.policyLevelCoverages,
    underwritingQuestions: existingEndorsement?.underwritingQuestions,
    savedValues,
  });

  const { data } = await client.mutate<
    CreateOrUpdateEndorsementMutation,
    CreateOrUpdateEndorsementMutationVariables
  >({
    mutation: CreateOrUpdateEndorsementDocument,
    // `projectedPolicy` is a hypothetical Policy. Normalizing it would overwrite
    // the real policy's cache entry (keyFields: policyLocator).
    fetchPolicy: "no-cache",
    variables: { input },
    // Keep the policy's endorsements list fresh after a save/reprice by
    // upserting the draft into the cached list (the mutation itself is no-cache).
    update: (cache, { data: mutationData, errors }) => {
      const upserted = mutationData?.createOrUpdateEndorsement;
      if (errors || !upserted?.isSuccessful || !policyLocator) return;
      upsertPolicyEndorsement(cache, policyLocator, input, upserted);
    },
  });

  const result = data?.createOrUpdateEndorsement;
  if (!result?.isSuccessful) {
    throw new Error(result?.reason ?? "Could not save the endorsement.");
  }

  // The save landed but Socotra did not return a price. The backend reports this
  // as a success with `priceChange` absent (`fetchEndorsementPriceChange` logs a
  // warning and returns `undefined`), so without this the agent would silently be
  // shown a stale or zero figure as if it were current.
  if (!result.priceChange) {
    datadogRum.addError(new Error("Endorsement saved without a price change"), {
      source: "endorsement.saveAndReprice",
      policyLocator,
      endorsementId: result.endorsementLocator ?? locator,
    });
    Toast.showWarning(
      "Saved, but the premium change could not be calculated. Use Check price to try again."
    );
  }

  return {
    ...(existingEndorsement ?? {}),
    endorsementId: result.endorsementLocator ?? locator ?? null,
    priceChange: result.priceChange ?? null,
    // Only one unit exists today, matching the rest of the flow.
    unitLevelCoverages:
      result.projectedPolicy?.units?.[0]?.unitLevelCoverages ??
      existingEndorsement?.unitLevelCoverages,
    policyLevelCoverages:
      result.projectedPolicy?.policyLevelCoverages ??
      existingEndorsement?.policyLevelCoverages,
    ...projectUnderwriting(result.projectedPolicy),
    // `insuredTabOverlay` is deliberately carried through untouched (by the
    // spread above). It is the *initial* hydration source and carries the
    // effective date and memo, so re-deriving it here would re-apply those — the
    // regression that blanked the effective date after a reprice.
    //
    // What the save persisted goes in `insuredTabProjection` instead. It is NOT
    // written back into the form: doing that overwrote values the agent had
    // typed. It is kept so a save can be reconciled against what was submitted.
    insuredTabProjection: result.projectedPolicy
      ? buildInsuredTabProjection(result.projectedPolicy)
      : existingEndorsement?.insuredTabProjection,
    additionalInterests: result.projectedPolicy
      ? result.projectedPolicy.additionalInterests ?? []
      : existingEndorsement?.additionalInterests,
  };
};

const initialState: EndorsementState = {
  loading: {
    show: false,
    messages: [],
  },
  loadingFromBackend: false,
  mode: "create",
  policyLocator: null,
  endorsementId: null,
  endorsement: undefined,
  endorsementLoadStatus: "idle",
  premiumDelta: null,
  deltaTax: null,
  deltaFees: null,
  lastConfirmedDelta: null,
  isPriceUnavailable: false,
  isRecalculating: false,
  hasUnsavedChanges: false,
  payment: { status: "idle", error: undefined },
  endorsementIssueBlockReason: undefined,
  isUnderwritingChecked: false,
  isCheckingUnderwriting: false,
  minDateEffectiveDate: undefined,
  maxDateEffectiveDate: undefined,
  eligibilityBlockReason: undefined,
  error: undefined,
};

const VIEW_ONLY_MODES: readonly EndorsementMode[] = ["issue", "review"];

const getEndorsementState = (getState: () => unknown): EndorsementState =>
  (getState() as RootWithEndorsement).endorsement.endorsement;

const EMPTY_ENDORSEMENT_FORM_DEFAULTS: Record<string, never> = {};

const FAILED_STATUS_BY_ISSUE_ERROR_TYPE: Partial<
  Record<string, EndorsementStatus>
> = {
  [IssueEndorsementStatus.PaymentError]: EndorsementStatus.PaymentFailed,
  ExecutionError: EndorsementStatus.IssuanceFailed,
  [IssueEndorsementStatus.ActiveRenewalError]: EndorsementStatus.IssuanceFailed,
  [IssueEndorsementStatus.DraftRenewalError]: EndorsementStatus.IssuanceFailed,
  [IssueEndorsementStatus.NonRenewalIssuedError]:
    EndorsementStatus.IssuanceFailed,
  [IssueEndorsementStatus.PolicyCancelledError]:
    EndorsementStatus.IssuanceFailed,
  [IssueEndorsementStatus.PolicyExpiredError]: EndorsementStatus.IssuanceFailed,
  [IssueEndorsementStatus.UnderwritingReferredError]:
    EndorsementStatus.AwaitingApproval,
  [IssueEndorsementStatus.UnderwritingDeclinedError]:
    EndorsementStatus.AwaitingApproval,
  [IssueEndorsementStatus.ConflictingEndorsementError]:
    EndorsementStatus.IssuanceFailed,
};

const setCachedEndorsementStatus = (
  cache: ApolloCache<unknown>,
  policyLocator: string,
  endorsementLocator: string,
  status: EndorsementStatus
) =>
  cache.modify({
    id: cache.identify({ __typename: "Policy", policyLocator }),
    fields: {
      endorsements(existing: (StoreObject | Reference)[] = [], { readField }) {
        return existing.map((endorsement) =>
          readField("endorsementLocator", endorsement) === endorsementLocator
            ? { ...endorsement, status }
            : endorsement
        );
      },
    },
  });

const upsertPolicyEndorsement = (
  cache: ApolloCache<unknown>,
  policyLocator: string,
  input: CreateOrUpdateEndorsementInput,
  result: CreateOrUpdateEndorsementMutation["createOrUpdateEndorsement"]
) => {
  const { endorsementLocator } = result;
  if (!endorsementLocator) return;

  cache.modify({
    id: cache.identify({ __typename: "Policy", policyLocator }),
    fields: {
      endorsements(existing: (StoreObject | Reference)[] = [], { readField }) {
        const current = existing.find(
          (item) => readField("endorsementLocator", item) === endorsementLocator
        );

        const saved = {
          __typename: "PolicyEndorsement",
          endorsementLocator,
          status:
            result.status ??
            readField<EndorsementStatus>("status", current) ??
            EndorsementStatus.InProgress,
          effectiveDate:
            input.effectiveDate ??
            readField<string>("effectiveDate", current) ??
            "",
          note: input.reason ?? readField<string>("note", current) ?? "",
          isSocotraNative: false,
        };

        return current
          ? existing.map((item) => (item === current ? saved : item))
          : [...existing, saved];
      },
    },
  });
};

export const endorsementSlice = createAsyncThunkSlice({
  name: "endorsement",
  initialState,
  selectors: {
    selectMode: (state) => state.mode,
    selectIsViewOnly: (state) => VIEW_ONLY_MODES.includes(state.mode),
    selectEndorsement: (state) => state.endorsement,
    selectIsUnderwritingFailed: (state) =>
      isUnderwritingFailed(state.endorsement),
    selectIsUnderwritingDeclined: (state) =>
      state.endorsement?.underwritingDecision ===
      EndorsementUnderwritingDecision.Declined,
    selectEndorsementId: (state) => state.endorsementId,
    selectPremiumDelta: (state) => state.premiumDelta,
    selectDeltaTax: (state) => state.deltaTax,
    selectDeltaFees: (state) => state.deltaFees,
    selectLastConfirmedDelta: (state) => state.lastConfirmedDelta,
    /**
     * Whether the shown delta is stale because the backend could not price the
     * last save/reprice. The footer uses it to avoid presenting the previous
     * figure as if it were current.
     */
    selectIsPriceUnavailable: (state) => state.isPriceUnavailable,
    selectIsRecalculating: (state) => state.isRecalculating,
    selectHasUnsavedChanges: (state) => state.hasUnsavedChanges,
    selectPaymentStatus: (state) => state.payment.status,
    selectPaymentError: (state) => state.payment.error,
    selectIsPaymentLoading: (state) => state.payment.status === "loading",
    selectEndorsementIssueBlockReason: (state) =>
      state.endorsementIssueBlockReason,
    selectIsUnderwritingChecked: (state) => state.isUnderwritingChecked,
    selectIsCheckingUnderwriting: (state) => state.isCheckingUnderwriting,
    selectShouldCheckUnderwriting: (state) =>
      state.mode !== "issue" ||
      state.endorsement?.status === EndorsementStatus.AwaitingApproval,
    selectNeedsUnderwritingReview: (state) =>
      needsUnderwritingReview(state.endorsement),
    selectUnderwritingNotes: (state) => state.endorsement?.underwritingNotes,
    /**
     * Allowed date window for the endorsement effective date.
     * `checkEndorsementEligibility` is the only source - bounds are undefined until it resolves.
     */
    selectEndorsementEffectiveDateBounds: createSelector(
      [
        (state: EndorsementState) => state.minDateEffectiveDate,
        (state: EndorsementState) => state.maxDateEffectiveDate,
      ],
      (min, max) => ({
        minDate: min ? parseISO(min) : undefined,
        maxDate: max ? parseISO(max) : undefined,
      })
    ),
    /** The blocking reason enum when endorsement eligibility check fails. */
    selectEligibilityBlockReason: (state) => state.eligibilityBlockReason,
    selectEndorsementLoading: (state) => state.loading,
    selectEndorsementLoadingFromBackend: (state) => state.loadingFromBackend,
    selectEndorsementLoadStatus: (state) => state.endorsementLoadStatus,
    selectEndorsementError: (state) => state.error,
    /** Whether the endorsement has been loaded from Socotra — used to guard later steps. */
    selectEndorsementPopulated: (state) => Boolean(state.endorsement),
    /** Projected unit-level coverages, source of the Coverage step's lists. */
    selectEndorsementUnitLevelCoverages: (state) =>
      state.endorsement?.unitLevelCoverages,
    selectEndorsementPolicyLevelCoverages: (state) =>
      state.endorsement?.policyLevelCoverages,
    selectEndorsementUnderwritingQuestions: (state) =>
      state.endorsement?.underwritingQuestions,
    selectEndorsementUnderwritingAnswers: (state) =>
      state.endorsement?.underwritingAnswers,
    /** Default values for the later-step react-hook-form, sourced from Socotra. */
    selectEndorsementFormDefaultValues: (state) =>
      state.endorsement ?? EMPTY_ENDORSEMENT_FORM_DEFAULTS,
  },
  reducers: (c) => {
    const { create, createWithType } = typedReducerCreator(c);

    return {
      /** Initialise policy/mode context from the route on layout mount. */
      setContext: create.reducer<{
        policyLocator: string;
        mode: EndorsementMode;
        endorsementId?: string | null;
        initialPremiumDelta?: number | null;
      }>((state, { payload }) => {
        state.policyLocator = payload.policyLocator;
        state.mode = payload.mode;
        state.endorsementId = payload.endorsementId ?? null;
        if (payload.mode !== "create") {
          state.premiumDelta = payload.initialPremiumDelta ?? null;
          state.lastConfirmedDelta = payload.initialPremiumDelta ?? null;
        }
      }),
      setMode: create.reducer<EndorsementMode>((state, { payload }) => {
        state.mode = payload;
      }),
      confirmPremiumDelta: create.reducer<number | null>(
        (state, { payload }) => {
          state.premiumDelta = payload;
          state.lastConfirmedDelta = payload;
        }
      ),
      revertPremiumDelta: create.reducer((state) => {
        state.premiumDelta = state.lastConfirmedDelta;
      }),
      setDeltaTax: create.reducer<number | null>((state, { payload }) => {
        state.deltaTax = payload;
      }),
      setDeltaFees: create.reducer<number | null>((state, { payload }) => {
        state.deltaFees = payload;
      }),
      setHasUnsavedChanges: create.reducer<boolean>((state, { payload }) => {
        state.hasUnsavedChanges = payload;
      }),
      resetUnderwritingChecked: create.reducer((state) => {
        state.isUnderwritingChecked = false;
      }),
      /** Store the earliest allowed effective date (ISO) from its query. */
      setMinDateEffectiveDate: create.reducer<string | undefined>(
        (state, { payload }) => {
          state.minDateEffectiveDate = payload;
        }
      ),
      setEndorsementId: create.reducer<string>((state, { payload }) => {
        state.endorsementId = payload;
      }),
      showLoadingFrame: create.reducer<ShowLoadingFramePayload>(
        (state, { payload }) => {
          state.loading = { show: true, ...payload };
        }
      ),
      hideLoadingFrame: create.reducer((state) => {
        state.loading = {
          show: false,
          messages: [],
          maxLoadTime: undefined,
          messageRotationInterval: undefined,
        };
      }),
      setEndorsementError: create.reducer<Error | undefined>(
        (state, { payload }) => {
          state.error = payload;
        }
      ),
      setEndorsementIssueBlockReason: create.reducer<
        IssueEndorsementStatus | undefined
      >((state, { payload }) => {
        state.endorsementIssueBlockReason = payload;
      }),
      resetEndorsement: create.reducer(() => initialState),

      // ── Thunks ──

      /**
       * Checks whether a new endorsement can be created for the given policy.
       * Returns the full eligibility response; when eligible also stores the
       * min/max effective dates in state.
       */
      checkEndorsementEligibility: createWithType<
        CheckEndorsementEligibilityQuery["checkEndorsementEligibility"],
        { policyLocator: string }
      >(
        async ({ policyLocator }, thunkAPI) => {
          const { data } =
            await thunkAPI.extra.client.query<CheckEndorsementEligibilityQuery>(
              {
                query: CheckEndorsementEligibilityDocument,
                variables: { policyLocator },
                fetchPolicy: "network-only",
              }
            );

          return data.checkEndorsementEligibility;
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
            state.eligibilityBlockReason = undefined;
            state.minDateEffectiveDate = undefined;
            state.maxDateEffectiveDate = undefined;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;
            if (payload.isEligible) {
              state.minDateEffectiveDate =
                payload.minEffectiveDate ?? undefined;
              state.maxDateEffectiveDate =
                payload.maxEffectiveDate ?? undefined;
            } else {
              state.minDateEffectiveDate = undefined;
              state.maxDateEffectiveDate = undefined;
              state.eligibilityBlockReason = payload.reason ?? undefined;
            }
          },
          rejected: (state, { payload, error }) => {
            state.error = (payload as Error) ?? (error as Error);
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),

      /**
       * Loads a saved endorsement draft via `getEndorsement`.
       *
       * Returns the draft's price change plus an `insuredTabOverlay` — the
       * subset of Insured Info values the draft can resolve, which the Insured
       * step's form merges over its policy baseline (see `EndorsementForm`).
       * The overlay is deliberately carried on the returned endorsement rather
       * than written into the `basicEndorsementDetails` slice: merging in the
       * form keeps it correct regardless of whether the policy seed or this
       * load lands first.
       */
      loadEndorsement: createWithType<
        EndorsementData,
        {
          policyLocator: string;
          mode: EndorsementMode;
          endorsementId?: string | null;
        }
      >(
        async ({ policyLocator, endorsementId }, thunkAPI) => {
          // Creating a brand-new draft: nothing to load yet, keep the policy
          // baseline the form already seeded.
          if (!endorsementId) {
            return { endorsementId: null };
          }

          thunkAPI.dispatch(
            endorsementSlice.actions.showLoadingFrame({
              messages: ["Loading endorsement…"],
              maxLoadTime: 30,
            })
          );

          try {
            const { data } = await thunkAPI.extra.client.query<
              GetEndorsementQuery,
              GetEndorsementQueryVariables
            >({
              query: GetEndorsementDocument,
              variables: { policyLocator, endorsementLocator: endorsementId },
              // `projectedPolicy` is a hypothetical Policy without its own
              // `policyLocator` in the selection. Normalizing it would throw on
              // the missing key field (keyFields: policyLocator) and, if keyed,
              // would clobber the real policy's cache entry — so never cache it
              // (mirrors the createOrUpdate mutation's `no-cache`).
              fetchPolicy: "no-cache",
            });

            const draft = data.getEndorsement;
            if (!draft) {
              throw new Error("Endorsement not found");
            }

            // Resolve the draft's saved edits into an Insured Info overlay the
            // form merges on top of the policy baseline. Carried on the loaded
            // endorsement so the merge stays in the form (react-hook-form),
            // independent of policy-seed vs draft-load order.
            const endorsement: EndorsementData = {
              endorsementId: draft.endorsementLocator,
              priceChange: draft.priceChange ?? null,
              status: draft.status ?? undefined,
              failureReason: draft.failureReason ?? undefined,
              underwritingDecision: draft.underwriting?.decision,
              underwritingNotes: draft.underwriting?.notes,
              insuredTabOverlay: buildInsuredTabOverlayFromDraft(draft),
              // The Coverage step reads its Extra Coverages from here. Opening a
              // draft has to supply them: they used to arrive only as a side
              // effect of `createOrUpdateEndorsement`, so the step rendered "no
              // extra coverages" until some save happened to run.
              unitLevelCoverages:
                draft.projectedPolicy?.units?.[0]?.unitLevelCoverages ??
                undefined,
              policyLevelCoverages:
                draft.projectedPolicy?.policyLevelCoverages ?? undefined,
              ...projectUnderwriting(draft.projectedPolicy),
            };

            return endorsement;
          } finally {
            thunkAPI.dispatch(endorsementSlice.actions.hideLoadingFrame());
          }
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
            state.endorsementLoadStatus = "pending";
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;
            state.endorsement = payload;
            state.endorsementLoadStatus = "succeeded";
            state.endorsementId = payload.endorsementId ?? state.endorsementId;
            // Hydrate the footer delta from the draft's saved price change. The
            // guard matters: a brand-new draft resolves to `{ endorsementId: null }`
            // with no `priceChange` key at all, which is "nothing saved yet"
            // rather than "could not be priced".
            if (payload.priceChange !== undefined) {
              applyPriceChange(state, payload.priceChange);
            }
            if (isUnderwritingFailed(payload)) {
              state.mode = "review";
            }
            if (state.mode === "issue") {
              state.endorsementIssueBlockReason = mapFailureReasonToIssueStatus(
                payload.failureReason
              );
            }
          },
          rejected: (state, { payload, error }) => {
            state.error = (payload as Error) ?? (error as Error);
            state.endorsementLoadStatus = "failed";
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),

      /**
       * Creates or updates the endorsement in Socotra via the unified
       * `createOrUpdateEndorsement` mutation. With no draft yet it opens one on
       * the policy; thereafter it updates the stored draft. Either way the draft
       * is re-quoted and the refreshed `priceChange` updates the footer delta.
       */
      createOrUpdateEndorsement: createWithType<
        EndorsementData,
        CreateOrUpdateEndorsementArgs
      >(
        async ({ values, unitId, savedValues }, thunkAPI) => {
          thunkAPI.dispatch(
            endorsementSlice.actions.showLoadingFrame({
              messages: ["Saving endorsement…"],
              maxLoadTime: 30,
            })
          );
          try {
            const { endorsement, endorsementId, policyLocator } =
              getEndorsementState(thunkAPI.getState);

            return await saveAndRepriceEndorsement({
              client: thunkAPI.extra.client,
              values,
              unitId,
              endorsementId,
              policyLocator,
              existingEndorsement: endorsement,
              savedValues,
            });
          } finally {
            thunkAPI.dispatch(endorsementSlice.actions.hideLoadingFrame());
          }
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          fulfilled: (state, { payload }) => {
            state.error = undefined;
            state.endorsement = payload;
            state.endorsementId = payload.endorsementId ?? state.endorsementId;
            state.hasUnsavedChanges = false;
            applyPriceChange(state, payload.priceChange);
          },
          rejected: (state, { payload, error }) => {
            const err = (payload as Error) ?? (error as Error);
            state.error =
              isApolloError(err) && (err as ApolloError).graphQLErrors.length
                ? (err as ApolloError).graphQLErrors[0]
                : err;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),

      /**
       * Reprices the endorsement against Socotra and returns the refreshed data.
       * Uses the same `createOrUpdateEndorsement` path as save (which always
       * re-quotes; the initial call creates the quoted draft), so the footer
       * "Check price" button works without a prior save.
       */
      checkPrice: createWithType<
        EndorsementData,
        CreateOrUpdateEndorsementArgs
      >(
        async ({ values, unitId, savedValues }, thunkAPI) => {
          const { endorsement, endorsementId, policyLocator } =
            getEndorsementState(thunkAPI.getState);

          try {
            return await saveAndRepriceEndorsement({
              client: thunkAPI.extra.client,
              values,
              unitId,
              endorsementId,
              policyLocator,
              existingEndorsement: endorsement,
              savedValues,
            });
          } catch (e) {
            const error =
              e instanceof Error ? e : new Error("unexpected error");
            // Report the real (possibly backend) error to Datadog RUM with
            // context for debugging; the agent only ever sees the friendly toast.
            datadogRum.addError(error, {
              source: "endorsement.checkPrice",
              policyLocator,
              endorsementId,
            });
            Toast.showError("Failed to recalculate premium. Please try again.");
            return thunkAPI.rejectWithValue(error.message);
          }
        },
        {
          pending: (state) => {
            state.isRecalculating = true;
          },
          fulfilled: (state, { payload }) => {
            state.endorsement = payload;
            state.endorsementId = payload.endorsementId ?? state.endorsementId;
            applyPriceChange(state, payload.priceChange);
            // `checkPrice` saves too.
            state.hasUnsavedChanges = false;
          },
          rejected: (state) => {
            // Revert to the last confirmed delta on failure.
            state.premiumDelta = state.lastConfirmedDelta;
          },
          settled: (state) => {
            state.isRecalculating = false;
          },
        }
      ),

      /**
       * Discards endorsements via the GraphQL mutation and drops them from the
       * policy's cached endorsement list. Shows an error toast on failure and,
       * unless `silent`, a success toast. Leaves the slice state alone: the flow
       * resets itself when the layout unmounts.
       */
      discardEndorsements: createWithType<
        DiscardEndorsementsMutation["discardEndorsements"],
        {
          policyLocator: string;
          endorsementLocators?: string[];
          discardAllExceptSelected?: boolean;
          silent?: boolean;
        }
      >(
        async (
          {
            policyLocator,
            endorsementLocators,
            discardAllExceptSelected,
            silent,
          },
          thunkAPI
        ) => {
          const fallbackMessage =
            "Could not discard endorsement. Please try again.";

          try {
            const { data } = await thunkAPI.extra.client.mutate<
              DiscardEndorsementsMutation,
              DiscardEndorsementsMutationVariables
            >({
              mutation: DiscardEndorsementsDocument,
              update: (cache, { data, errors }) => {
                if (errors || !data) {
                  return;
                }
                const discardedLocators = data.discardEndorsements.results
                  .filter(({ isSuccessful }) => isSuccessful)
                  .map(({ endorsementLocator }) => endorsementLocator);

                cache.modify({
                  id: cache.identify({
                    __typename: "Policy",
                    policyLocator,
                  }), // the specific Policy object
                  fields: {
                    endorsements(existingEndorsements = [], { readField }) {
                      return existingEndorsements.filter(
                        (endorsementRef: StoreObject | Reference) =>
                          !discardedLocators.includes(
                            readField("endorsementLocator", endorsementRef)
                          )
                      );
                    },
                  },
                });
              },
              variables: {
                policyLocator,
                endorsementLocators,
                discardAllExceptSelected,
              },
            });

            if (!data?.discardEndorsements) {
              return thunkAPI.rejectWithValue(
                "No data returned from discardEndorsements mutation"
              );
            }

            const failed = data.discardEndorsements.results.find(
              ({ isSuccessful }) => !isSuccessful
            );

            if (failed) {
              const message = failed.errorReason ?? fallbackMessage;
              Toast.showError(message);
              return thunkAPI.rejectWithValue(message);
            }

            if (!silent) {
              Toast.showSuccess("Endorsement was successfully discarded!");
            }

            return data.discardEndorsements;
          } catch (e) {
            const message =
              e instanceof ApolloError
                ? e.graphQLErrors[0]?.message ?? fallbackMessage
                : fallbackMessage;

            Toast.showError(message);
            return thunkAPI.rejectWithValue(message);
          }
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
          },
          rejected: (state) => {
            // Keep the current state intact on failure.
            state.loadingFromBackend = false;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),

      checkEndorsementUnderwriting: createWithType<
        CheckEndorsementUnderwritingQuery["checkEndorsementUnderwriting"],
        void
      >(
        async (_arg, thunkAPI) => {
          const { endorsementId, policyLocator } = getEndorsementState(
            thunkAPI.getState
          );

          if (!endorsementId) {
            return thunkAPI.rejectWithValue(undefined);
          }

          thunkAPI.dispatch(
            endorsementSlice.actions.showLoadingFrame({
              messages: ["Checking underwriting..."],
              maxLoadTime: 30,
            })
          );

          try {
            const { data } = await thunkAPI.extra.client.query<
              CheckEndorsementUnderwritingQuery,
              CheckEndorsementUnderwritingQueryVariables
            >({
              query: CheckEndorsementUnderwritingDocument,
              variables: { endorsementLocator: endorsementId },
              fetchPolicy: "network-only",
            });

            const result = data.checkEndorsementUnderwriting;

            if (policyLocator) {
              setCachedEndorsementStatus(
                thunkAPI.extra.client.cache,
                policyLocator,
                endorsementId,
                result.status
              );
            }

            return result;
          } catch {
            Toast.showError(
              `Something went wrong. Please try again or contact ${AGENT_HELP_ADDRESS}.`
            );

            return thunkAPI.rejectWithValue(undefined);
          } finally {
            thunkAPI.dispatch(endorsementSlice.actions.hideLoadingFrame());
          }
        },
        {
          pending: (state) => {
            state.endorsementIssueBlockReason = undefined;
            state.isUnderwritingChecked = true;
            state.isCheckingUnderwriting = true;
          },
          fulfilled: (state, { payload }) => {
            if (!state.endorsement) return;
            state.endorsement.status = payload.status;
            state.endorsement.underwritingDecision = payload.decision;
            state.endorsement.underwritingNotes = payload.notes;
            if (isUnderwritingFailed(state.endorsement)) {
              state.mode = "review";
            } else if (state.mode === "review") {
              state.mode = "issue";
            }
          },
          settled: (state) => {
            state.isCheckingUnderwriting = false;
          },
        }
      ),

      /**
       * Issues the endorsement stored in state and patches the policy's cached
       * endorsement list so the policy page shows it as applied. Rejects with
       * the `IssueEndorsementStatus` the backend named, which the checkout step
       * renders as the block reason.
       */
      issueEndorsement: createWithType<
        IssueEndorsementMutation["issueEndorsement"],
        void
      >(
        async (_arg, thunkAPI) => {
          const { endorsementId, policyLocator } = getEndorsementState(
            thunkAPI.getState
          );

          if (!endorsementId) {
            return thunkAPI.rejectWithValue(IssueEndorsementStatus.GlobalError);
          }

          thunkAPI.dispatch(
            endorsementSlice.actions.showLoadingFrame({
              messages: ["Submitting..."],
              maxLoadTime: 30,
            })
          );

          try {
            const { data } = await thunkAPI.extra.client.mutate<
              IssueEndorsementMutation,
              IssueEndorsementMutationVariables
            >({
              mutation: IssueEndorsementDocument,
              variables: { endorsementLocator: endorsementId },
              update: (cache, { data, errors }) => {
                const result = data?.issueEndorsement;
                if (errors || !result || !policyLocator) {
                  return;
                }

                cache.modify({
                  id: cache.identify({ __typename: "Policy", policyLocator }),
                  fields: {
                    endorsements(existingEndorsements = [], { readField }) {
                      return existingEndorsements.map(
                        (endorsement: StoreObject | Reference) =>
                          readField("endorsementLocator", endorsement) ===
                          result.endorsementLocator
                            ? {
                                ...endorsement,
                                status: EndorsementStatus.Issued,
                                effectiveDate:
                                  result.effectiveDate ??
                                  readField("effectiveDate", endorsement),
                              }
                            : endorsement
                      );
                    },
                  },
                });
              },
            });

            const result = data?.issueEndorsement;

            Toast.showSuccess("Endorsement issued successfully.");
            return result;
          } catch (e) {
            const errorType = getGraphQLErrorType(e);
            const blockReason = Object.values(IssueEndorsementStatus).find(
              (status) => status === errorType
            );

            const failedStatus = errorType
              ? FAILED_STATUS_BY_ISSUE_ERROR_TYPE[errorType]
              : undefined;

            if (failedStatus && policyLocator) {
              setCachedEndorsementStatus(
                thunkAPI.extra.client.cache,
                policyLocator,
                endorsementId,
                failedStatus
              );
            }

            Toast.showError("Endorsement could not be issued.");
            return thunkAPI.rejectWithValue(
              blockReason ?? IssueEndorsementStatus.GlobalError
            );
          } finally {
            thunkAPI.dispatch(endorsementSlice.actions.hideLoadingFrame());
          }
        },
        {
          pending: (state) => {
            state.loadingFromBackend = true;
            state.endorsementIssueBlockReason = undefined;
          },
          fulfilled: (state) => {
            state.error = undefined;
            state.hasUnsavedChanges = false;
          },
          rejected: (state, { payload }) => {
            state.endorsementIssueBlockReason =
              (payload as IssueEndorsementStatus) ??
              IssueEndorsementStatus.GlobalError;
          },
          settled: (state) => {
            state.loadingFromBackend = false;
          },
        }
      ),
    };
  },
});

export const {
  actions: {
    setContext,
    setMode,
    confirmPremiumDelta,
    revertPremiumDelta,
    setDeltaFees,
    setDeltaTax,
    setHasUnsavedChanges,
    resetUnderwritingChecked,
    setMinDateEffectiveDate,
    setEndorsementId,
    showLoadingFrame,
    hideLoadingFrame,
    setEndorsementError,
    setEndorsementIssueBlockReason,
    resetEndorsement,
    checkEndorsementEligibility,
    loadEndorsement,
    createOrUpdateEndorsement,
    checkPrice,
    discardEndorsements,
    checkEndorsementUnderwriting,
    issueEndorsement,
  },
  selectors: {
    selectMode,
    selectIsViewOnly,
    selectEndorsement,
    selectIsUnderwritingFailed,
    selectIsUnderwritingDeclined,
    selectEndorsementId,
    selectPremiumDelta,
    selectDeltaFees,
    selectDeltaTax,
    selectLastConfirmedDelta,
    selectIsPriceUnavailable,
    selectIsRecalculating,
    selectHasUnsavedChanges,
    selectPaymentStatus,
    selectPaymentError,
    selectIsPaymentLoading,
    selectEndorsementIssueBlockReason,
    selectIsUnderwritingChecked,
    selectIsCheckingUnderwriting,
    selectShouldCheckUnderwriting,
    selectNeedsUnderwritingReview,
    selectUnderwritingNotes,
    selectEndorsementEffectiveDateBounds,
    selectEligibilityBlockReason,
    selectEndorsementLoading,
    selectEndorsementLoadingFromBackend,
    selectEndorsementLoadStatus,
    selectEndorsementError,
    selectEndorsementPopulated,
    selectEndorsementUnitLevelCoverages,
    selectEndorsementPolicyLevelCoverages,
    selectEndorsementUnderwritingQuestions,
    selectEndorsementUnderwritingAnswers,
    selectEndorsementFormDefaultValues,
  },
} = endorsementSlice;

export default endorsementSlice;
